/**
 * API client — adapted from frontend/js/services/api.js
 *
 * Handles Bearer token injection, 401 redirect, and consistent error shape.
 */

const BASE_URL = '/api';

export interface ApiError {
  message: string;
  status: number;
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Machine-readable code from the body's `error` field (e.g. `payslip_stale`), when the backend sends one. */
    public readonly code?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

function getToken(): string | null {
  // Read from Zustand's persisted auth store in localStorage
  try {
    const raw = localStorage.getItem('casalary-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { token?: string } };
    return parsed?.state?.token ?? null;
  } catch {
    return null;
  }
}

function handleUnauthorized(): void {
  // Clear persisted auth and redirect to login
  localStorage.removeItem('casalary-auth');
  window.location.href = '/login';
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers ?? {}),
  };

  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    let code: string | undefined;
    try {
      // `code` is the dedicated machine tag when the backend sends one (the contract
      // routes: `stale`, `wrong_state`, `not_editable`); older routes put the tag in
      // `error` itself (`payslip_stale`, `upgrade_required`), so that stays the fallback.
      const body = (await response.json()) as {
        message?: string;
        error?: string;
        code?: string;
        details?: { field?: string; message?: string }[];
      };
      message = body.message ?? body.error ?? message;
      code = body.code ?? body.error;
      // A 400 from the validation middleware says only "Validation failed" at the top;
      // the reason is in `details`. Surface the first one, or the user sees a toast
      // that names no field and cannot act on it.
      const detail = Array.isArray(body.details) ? body.details.find((d) => d?.message)?.message : undefined;
      if (detail) message = `${message}: ${detail}`;
    } catch {
      // ignore parse error
    }
    if (response.status === 401 && getToken()) {
      // Session expired on a protected endpoint — clear auth and redirect
      handleUnauthorized();
    }
    if (response.status === 402) {
      // A plan gate blocked this action — let a global listener show the upgrade dialog.
      window.dispatchEvent(new CustomEvent('casalary:upgrade-required', { detail: { message, code } }));
    }
    throw new HttpError(response.status, message, code);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const apiClient = {
  get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'GET' });
  },

  post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
  },

  put<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
  },

  patch<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    });
  },

  delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'DELETE' });
  },
};
